'use client';

import { useCallback, useRef } from 'react';
import { ANIMATION_DURATIONS, type AnimationAction, type RainaAnimation } from './useAnimationState';

// Safety margin for fallback timeout
const ANIMATION_FALLBACK_BUFFER_MS = 50;

export interface UseAnimationTriggersOptions {
  dispatch: React.Dispatch<AnimationAction>;
  canStartAnimation: (animation: RainaAnimation) => boolean;
  onAnimationComplete?: (animation: RainaAnimation) => void;
}

export interface UseAnimationTriggersResult {
  triggerWave: () => void;
  triggerBlink: () => void;
  triggerNod: () => void;
  triggerEarTwitch: () => void;
  triggerHeadTilt: () => void;
  handleAnimationEnd: (animation: RainaAnimation) => void;
}

/**
 * Hook for triggering Raina animations with fallback timeouts.
 *
 * Provides trigger functions for each animation type and handles
 * animation end callbacks with fallback timeout cleanup.
 */
export function useAnimationTriggers({
  dispatch,
  canStartAnimation,
  onAnimationComplete,
}: UseAnimationTriggersOptions): UseAnimationTriggersResult {
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  onAnimationCompleteRef.current = onAnimationComplete;

  // Fallback timeouts in case onAnimationEnd doesn't fire (e.g., element unmounted mid-animation)
  const fallbackTimeoutRef = useRef<Map<RainaAnimation, ReturnType<typeof setTimeout>>>(new Map());

  const startAnimation = useCallback(
    (anim: RainaAnimation, direction?: 'left' | 'right') => {
      if (canStartAnimation(anim)) {
        dispatch({ type: 'START', animation: anim, direction });

        if (anim !== 'idle') {
          const existing = fallbackTimeoutRef.current.get(anim);
          if (existing) clearTimeout(existing);

          const timeout = setTimeout(() => {
            dispatch({ type: 'END', animation: anim });
            onAnimationCompleteRef.current?.(anim);
          }, ANIMATION_DURATIONS[anim] + ANIMATION_FALLBACK_BUFFER_MS);

          fallbackTimeoutRef.current.set(anim, timeout);
        }
      } else {
        dispatch({ type: 'QUEUE', animation: anim });
      }
    },
    [dispatch, canStartAnimation],
  );

  const handleAnimationEnd = useCallback(
    (anim: RainaAnimation) => {
      const fallback = fallbackTimeoutRef.current.get(anim);
      if (fallback) {
        clearTimeout(fallback);
        fallbackTimeoutRef.current.delete(anim);
      }

      dispatch({ type: 'END', animation: anim });
      onAnimationCompleteRef.current?.(anim);
    },
    [dispatch],
  );

  const triggerWave = useCallback(() => startAnimation('wave'), [startAnimation]);
  const triggerBlink = useCallback(() => startAnimation('blink'), [startAnimation]);
  const triggerNod = useCallback(() => startAnimation('nod'), [startAnimation]);
  const triggerEarTwitch = useCallback(() => startAnimation('earTwitch'), [startAnimation]);
  const triggerHeadTilt = useCallback(() => {
    const direction = Math.random() < 0.5 ? 'left' : 'right';
    startAnimation('headTilt', direction);
  }, [startAnimation]);

  return {
    triggerWave,
    triggerBlink,
    triggerNod,
    triggerEarTwitch,
    triggerHeadTilt,
    handleAnimationEnd,
  };
}
